import React, { useState, useEffect } from 'react'
import axios from 'axios';
import dompurify from 'dompurify'
import { VoteGroup } from './VoteGroup'
import { LoadingButton } from '../common/LoadingButton'
import { InfoBanner } from '../common/InfoBanner'
import { MultiGroupVoteSummary } from './MultiGroupVoteSummary'
import { useVoting } from '../../contexts/VotingContext';

export const MultiGroupVote = ({ receipt, personalId, getGroup, currentGroup, currentGroupVote, groupIds, handleSubmit, handleSubmitVotes,
    isPreview, previewId, handleOptionsUpdate, handleVoteError, isJustSubmitted, setJustSubmitted }) => {
    const { ballot, voter } = useVoting();
    const sanitizer = dompurify.sanitize;

    const [isStartLoading, setStartLoading] = useState(false)
    const [isBackLoading, setBackLoading] = useState(false)
    const [isNextLoading, setNextLoading] = useState(false)
    const [isSkipLoading, setSkipLoading] = useState(false)
    const [isSubmitLoading, setSubmitLoading] = useState(false)
    const [isStart, setStart] = useState(true)
    const [isSummary, setSummary] = useState(false)
    const [isFirstGroup, setFirstGroup] = useState(false)
    const [previewVotes, setPreviewVotes] = useState({})
    const [groups, setGroups] = useState({})
    const [groupVotes, setGroupVotes] = useState(null)

    useEffect(() => {
        if (currentGroupVote.hasVoted && !ballot.isVoteChange) {
            setStart(false)
            showSummary()
        }
    }, []);

    const handleSkip = (e) => {
        setFirstGroup(false)

        // Clear preview votes
        previewVotes[currentGroup.groupId] = []

        // Save group
        groups[currentGroup.groupId] = { ...currentGroup }

        axios.post('/vote/skip', {
            id: voter.uniqueId,
            key: voter.guid,
            groupId: currentGroup.groupId,
            receipt,
            personalId,
            ballotId: ballot.id,
            previewId,
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    handleVoteError("")

                    // Check for final group and show summary
                    if (isLastGroup()) {
                        showSummary()
                    }
                    else {
                        // Find next group ID
                        let nextGroupId = getGroupId(1)

                        // Get next group
                        getGroup(nextGroupId, previewVotes[nextGroupId], null, () => {
                            setSkipLoading(false)
                        })
                    }
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const handleNext = (e) => {
        setNextLoading(true)
        handleSubmit(false, (votes) => {
            // Saves votes if preview
            if (isPreview) {
                previewVotes[currentGroup.groupId] = [...votes]
            }

            // Check for final group and show summary
            if (isLastGroup()) {
                if (ballot.isSubmitVotesEachGroup) {
                    setJustSubmitted(true)
                }
                showSummary()
            }
            else {
                // Find next group ID
                let nextGroupId = getGroupId(1)

                // Get next group
                getGroup(nextGroupId, previewVotes[nextGroupId], null, () => {
                    setNextLoading(false)
                })
            }
            setFirstGroup(false)
        }, () => setNextLoading(false))
    }

    const handleBack = () => {
        setBackLoading(true)

        let backGroupId = 0
        if (isSummary) {
            // Get last in group ID list
            backGroupId = groupIds[groupIds.length - 1]
        }
        else {
            // Find previous group ID
            backGroupId = getGroupId(-1)
        }

        // Get last group
        getGroup(backGroupId, previewVotes[backGroupId], null, () => {
            setBackLoading(false)
            setSummary(false)
            checkFirstGroup(backGroupId)
        })
    }

    const checkFirstGroup = (groupId) => {
        let groupIndex = groupIds.indexOf(groupId)
        setFirstGroup(groupIndex === 0)
    }

    const isLastGroup = () => {
        let groupIndex = groupIds.indexOf(currentGroup.groupId)
        return groupIndex === groupIds.length - 1
    }

    const getGroupId = (increment) => {
        // Find next group ID
        let groupIndex = groupIds.indexOf(currentGroup.groupId)
        return groupIds[groupIndex + increment]
    }

    const handleMultiGroupSubmit = (e) => {
        setSubmitLoading(true)
        if (groupVotes) {
            // Convert groupVotes from map to array
            let votes = [].concat(...Object.values(groupVotes));

            // Submit
            handleSubmitVotes(votes, true, () => {
                if (ballot.isVoteChange) {
                    handleStart()
                }
                setSubmitLoading(false)
            }, () => setSubmitLoading(false))
        }
    }

    const showSummary = () => {
        // Get group votes
        axios.post('/vote/groupVotes', {
            id: voter.uniqueId,
            key: voter.guid,
            previewId,
            ballotId: ballot.id,
            receipt,
            personalId
        })
            .then((response) => {
                if (response.data.isSuccessful) {
                    // If preview grab votes from state
                    if (isPreview) {
                        setGroupVotes(previewVotes)
                    }
                    else {
                        let votes = response.data.payload.votes

                        // Convert to map based on group ID
                        let votesMap = votes.reduce((acc, vote) => {
                            // Add empty array for new ID
                            if (!acc[vote.groupId]) {
                                acc[vote.groupId] = [];
                            }
                            acc[vote.groupId].push({ ...vote });
                            return acc;
                        }, {});
                        setGroupVotes(votesMap)
                    }

                    let groups = response.data.payload.groups

                    // Convert to map based on group ID
                    let groupsMap = groups.reduce((acc, group) => {
                        acc[group.groupId] = { ...group };
                        return acc;
                    }, {});

                    setGroups(groupsMap)
                    setSummary(true)
                    setNextLoading(false)
                }
                else {
                    handleVoteError(response.data.errorMessage)
                }
            })
            .catch(function (error) {
                alert(error)
            })
    }

    const handleStart = () => {
        setStartLoading(true)
        setFirstGroup(true)
        setSummary(false)
        setStart(false)
        setStartLoading(false)
    }

    return (
        <React.Fragment>
            {isStart &&
                <React.Fragment>
                    <InfoBanner isSetHtml="true">
                        {ballot.groupVotingStatement}
                    </InfoBanner>

                    <div className="mb-4">
                        <span dangerouslySetInnerHTML={{ __html: sanitizer(ballot.ballotStatement) }} />
                    </div>

                    <LoadingButton className="btn"
                        onBtnClick={handleStart}
                        btnText="Start Voting"
                        isLoading={isStartLoading} />
                </React.Fragment>
            }
            {!isStart &&
                <form className="mb-4">
                    {!isSummary &&
                        <React.Fragment>
                            <VoteGroup currentGroup={currentGroup}
                                currentGroupVote={currentGroupVote}
                                handleOptionsUpdate={handleOptionsUpdate}
                                ballot={ballot}
                                isJustSubmitted={isJustSubmitted} />
                            {!isFirstGroup && !ballot.isSubmitVotesEachGroup &&
                                <LoadingButton className="btn mr-2"
                                    onBtnClick={handleBack}
                                    btnText="Back"
                                    isLoading={isBackLoading} />
                            }
                            {!currentGroup.isRequired &&
                                <LoadingButton className="btn mr-2"
                                    onBtnClick={handleSkip}
                                    btnText="Skip"
                                    isLoading={isSkipLoading}
                                    confirmText="Are you sure you want to skip this group? Anything entered in this group will not be recorded." />
                            }
                            <LoadingButton className="btn"
                                onBtnClick={handleNext}
                                btnText="Next"
                                isLoading={isNextLoading}
                                confirmText={ballot.isSubmitVotesEachGroup ? "Are you sure you want to submit your vote preferences?" : ""} />
                        </React.Fragment>
                    }

                    {isSummary &&
                        <React.Fragment>
                            <MultiGroupVoteSummary
                                groups={groups}
                                groupVotes={groupVotes} />
                            {(!currentGroupVote.hasVoted || ballot.isVoteChange) && !ballot.isSubmitVotesEachGroup &&
                                < React.Fragment >
                                    <LoadingButton className="btn mr-2"
                                        onBtnClick={handleBack}
                                        btnText="Back"
                                        isLoading={isBackLoading} />
                                    <LoadingButton className="btn"
                                        onBtnClick={handleMultiGroupSubmit}
                                        btnText="Submit"
                                        isLoading={isSubmitLoading}
                                        isSubmit="true"
                                        isDisabled={isPreview}
                                        confirmText="Are you sure you want to submit your vote preferences?" />
                                </React.Fragment>
                            }
                        </React.Fragment>}
                </form>
            }
        </React.Fragment >
    );
}